import { cx } from 'class-variance-authority';
import { UserAvatar } from 'components/User/UserAvatar';
import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface TrainerSelectionTrainer {
    id: string | number;
    name: string;
    description?: string | null;
    image_id?: number | null;
}

interface Props {
    trainers: TrainerSelectionTrainer[];
    name?: string;
    label?: string;
    /** trainer mode: the space only sells lessons, so "Kein Trainer" is not an option */
    required?: boolean;
}

const TrainerSelection = ({ trainers, name = 'trainer_id', label, required }: Props) => {
    const { t } = useTranslation('space-manager');
    const {
        field: { value, onChange }
    } = useController({
        name,
        defaultValue: null
    });
    const resolvedLabel = label ?? t('trainer.trainerLabel');

    const selectedValue = value == null || value === '' ? null : String(value);

    return (
        <div className="tw-space-y-2">
            <h4 className="tw-mb-0 tw-text-sm tw-font-medium">{resolvedLabel}</h4>
            <div className="tw-grid tw-grid-cols-1 tw-gap-2 sm:tw-grid-cols-2">
                {!required && (
                    <button
                        type="button"
                        onClick={() => onChange(null)}
                        className={cx(
                            selectedValue === null && 'tw-border-success tw-bg-success/10',
                            'tw-card-flat tw-cursor-pointer tw-border tw-border-solid tw-border-accents-2 tw-text-left tw-transition-colors'
                        )}
                    >
                        <span className="tw-block tw-font-semibold">{t('trainer.noTrainer')}</span>
                        <span className="tw-text-xs tw-text-accents-6">{t('trainer.noTrainerHint')}</span>
                    </button>
                )}
                {trainers.map((trainer) => {
                    const trainerValue = String(trainer.id);
                    const isSelected = selectedValue === trainerValue;

                    return (
                        <button
                            key={trainer.id}
                            type="button"
                            // stored as a string so the selected-state comparison above matches
                            onClick={() => onChange(required ? trainerValue : isSelected ? null : trainerValue)}
                            className={cx(
                                isSelected && 'tw-border-success tw-bg-success/10',
                                'tw-card-flat tw-flex tw-cursor-pointer tw-items-start tw-gap-3 tw-border tw-border-solid tw-border-accents-2 tw-text-left tw-transition-colors'
                            )}
                        >
                            <div className="tw-w-12 tw-shrink-0">
                                <UserAvatar
                                    user={{
                                        id: Number(trainer.id) || 0,
                                        name: trainer.name,
                                        image_id: trainer.image_id ?? undefined
                                    }}
                                    size={80}
                                />
                            </div>
                            <div className="tw-min-w-0 tw-flex-1">
                                <span className="tw-block tw-truncate tw-font-semibold">{trainer.name}</span>
                                <span className="tw-block tw-text-xs tw-text-accents-6">
                                    {trainer.description || t('trainer.noDescription')}
                                </span>
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default TrainerSelection;
